import React, { useState, useContext } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import AuthContext from './context/AuthContext';
import apiClient from './api';
import {
    TextField, Button, Box, Typography, Container, Collapse, CircularProgress,
    Snackbar, Alert, Stack,
} from '@mui/material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { glass, brand } from './theme';

function Register() {
    const [formData, setFormData] = useState({
        user_type: 'provider',
        first_name: '',
        phone_number: '',
        national_code: '',
        birth_date: '',
        birth_place: '',
    });
    const [otpCode, setOtpCode] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const { loginUser } = useContext(AuthContext);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleRequestOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        setSnackbar({ open: false, message: '', severity: 'success' });
        try {
            await apiClient.post('/api/auth/otp/request/', { phone_number: formData.phone_number });
            setOtpSent(true);
            setMessage('کد تایید به شماره شما ارسال شد.');
        } catch (error) {
            setMessage(error.response?.data?.detail || 'خطا در ارسال کد. شماره موبایل را بررسی کنید.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtpAndRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');
        setSnackbar({ open: false, message: '', severity: 'success' });

        const registrationData = { ...formData, otp_code: otpCode };

        try {
            const response = await apiClient.post('/api/auth/otp/verify/', registrationData);
            loginUser(response.data.tokens);
            setSnackbar({ open: true, message: 'ثبت نام و ورود با موفقیت انجام شد!', severity: 'success' });
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (detail && detail.includes("Invalid or expired OTP")) {
                setMessage("کد تایید اشتباه یا منقضی شده است.");
            } else if (detail) {
                setMessage(detail);
            } else {
                setMessage('خطا در تایید کد یا ثبت نام.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') { return; }
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <Box sx={{ background: glass.canvasGradient, minHeight: '100%', display: 'flex', alignItems: 'center', py: 6 }}>
            <Container component="main" maxWidth="sm">
                <Box sx={{ ...glass.card, p: { xs: 2.5, md: 4 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1 }}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '50%', bgcolor: brand.accentTint, color: brand.accentDeep,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <HandshakeIcon />
                        </Box>
                        <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
                            {otpSent ? 'تایید کد و تکمیل ثبت‌نام' : 'ثبت‌نام به‌عنوان همیار'}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 4 }}>
                        <VerifiedUserIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: .5, color: 'primary.main' }} />
                        اطلاعات شما فقط برای احراز هویت و اعتمادسازی با خانواده‌ها استفاده می‌شود.
                    </Typography>
                    <Box component="form" onSubmit={otpSent ? handleVerifyOtpAndRegister : handleRequestOtp}>
                        <Stack spacing={2.5}>
                            <TextField fullWidth required label="نام" name="first_name" value={formData.first_name} onChange={handleChange} disabled={otpSent || loading} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                            <TextField fullWidth required label="شماره موبایل" name="phone_number" type="tel" value={formData.phone_number} onChange={handleChange} disabled={otpSent || loading} inputProps={{ style: { textAlign: 'center' } }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                            <TextField fullWidth required label="کد ملی" name="national_code" value={formData.national_code} onChange={handleChange} disabled={otpSent || loading} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                            <TextField fullWidth required label="تاریخ تولد (مثال: ۱۳۶۵/۰۲/۱۵)" name="birth_date" value={formData.birth_date} onChange={handleChange} disabled={otpSent || loading} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                            <TextField fullWidth required label="محل تولد" name="birth_place" value={formData.birth_place} onChange={handleChange} disabled={otpSent || loading} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />

                            <Collapse in={otpSent} timeout="auto" unmountOnExit>
                                <TextField fullWidth required={otpSent} label="کد تایید ۶ رقمی" name="otp_code" type="number" value={otpCode} onChange={(e) => setOtpCode(e.target.value)} disabled={loading} inputProps={{ style: { textAlign: 'center', letterSpacing: '0.5em' } }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                            </Collapse>

                            {message && <Typography color={message.includes('خطا') || message.includes('اشتباه') ? 'error' : 'info'} sx={{ textAlign: 'center' }}>{message}</Typography>}

                            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}>
                                {loading ? <CircularProgress size={26} color="inherit" /> : (otpSent ? 'تایید و ثبت نام' : 'دریافت کد تایید')}
                            </Button>
                        </Stack>
                    </Box>
                    {!otpSent && (
                        <Typography variant="body2" sx={{ textAlign: 'center', mt: 3 }}>
                            قبلاً ثبت‌نام کرده‌اید؟{' '}
                            <Box component={RouterLink} to="/login" sx={{ color: 'primary.main', fontWeight: 700, textDecoration: 'none' }}>
                                از اینجا وارد شوید
                            </Box>
                        </Typography>
                    )}
                </Box>
                <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </Box>
    );
}

export default Register;
